import { mapService } from "@/services/map/mapService";
import store from "@/store";
import { attachRasterLoadTracking, attachTileLoadTracking, attachVectorLoadTracking, beginLayerLoad, endLayerLoad } from "@/services/layers/rasterProgressSource.js";
import { longRequestTileLoader } from "@/services/layers/tileLoader.js";
import TileWMS from "ol/source/TileWMS.js";
import {
    Tile as TileLayer,
    Vector as VectorLayer,
} from "ol/layer.js";
import { ImageArcGISRest, TileArcGISRest } from "ol/source.js";
import ImageLayer from 'ol/layer/Image.js';
import VectorSource from "ol/source/Vector";
import EsriJSON from "ol/format/EsriJSON";
import GeoJSON from "ol/format/GeoJSON.js";
import ImageWMS from 'ol/source/ImageWMS.js';
import { bbox as bboxStrategy } from "ol/loadingstrategy";
import { transformExtent } from "ol/proj";
import Polygon from "ol/geom/Polygon.js";
import { UrlHandler } from "@/services/utils/UrlHandler.js"
import { attributesTableService } from "@/services/layers/attributesTable.js";
import {
    IMPORT_PROVIDER,
    createImportedDescriptor,
    importedLayerRegistry,
} from "@/services/layers/importedLayerRegistry.js";
import { parseImportedFile } from "@/services/layers/importedFileParser.js";
import { padExtent } from "@/services/helpers/bboxHelper.js";
import { extend as extendExtent, createEmpty as createEmptyExtent } from "ol/extent.js";


const parsedImportedFiles = new Map();

// Cada encendido recibe un token. Apagar la capa invalida el token aunque la
// consulta inicial al proveedor todavía no haya terminado; así una respuesta
// tardía no puede volver a agregar al mapa una capa que el usuario ya apagó.
const pendingLayerActivations = new Map();

function activationKey(layerId) {
    return String(layerId);
}

function startLayerActivation(layerId) {
    const key = activationKey(layerId);
    const previous = pendingLayerActivations.get(key);
    if (previous) previous.cancelled = true;
    const token = { cancelled: false };
    pendingLayerActivations.set(key, token);
    return token;
}

function isCurrentLayerActivation(layerId, token) {
    return Boolean(token) && !token.cancelled &&
        pendingLayerActivations.get(activationKey(layerId)) === token;
}

function finishLayerActivation(layerId, token) {
    const key = activationKey(layerId);
    if (pendingLayerActivations.get(key) === token) {
        pendingLayerActivations.delete(key);
    }
}

function cancelLayerActivation(layerId) {
    const key = activationKey(layerId);
    const token = pendingLayerActivations.get(key);
    if (token) token.cancelled = true;
    pendingLayerActivations.delete(key);
    endLayerLoad({ layerId });
}

// Cache de geometryType ArcGIS por layer.id — solo éxitos; fallos reintentan
const layerTypeArcgisCache = new Map();

// buildPointSldBody arma un SLD 1.0.0 mínimo para el estilo de una
// Capa_Terreno (color/tamaño/ícono/opacidad de "Estilo por mapa", tarea
// 13.1). GeoServer no tiene ningún estilo propio asociado a ese dato — vive
// solo en Mongo (terrain_map_layer_settings) — así que en vez de subir un
// named style por REST, se manda como SLD_BODY en cada request WMS
// (buildGeoserverVectorLayer). SLD PointSymbolizer solo dibuja esta lista
// fija de WellKnownName, que es exactamente lo que el editor ofrece
// (terrainLayerIconCatalog en el backend) — nunca un ícono de FontAwesome
// arbitrario, porque GeoServer no tiene forma de dibujar eso.
const SLD_WELL_KNOWN_NAMES = new Set(["circle", "square", "triangle", "star", "cross", "x"]);
const DEFAULT_TERRAIN_MARKER_SIZE = 24;
// El backend consulta al GeoServer de la capa para responder este tipo. Si ese
// GeoServer no responde, la consulta queda colgada; sin este corte la activación
// se quedaba esperando indefinidamente.
const GEOSERVER_LAYERTYPE_TIMEOUT_MS = 15000;
const buildPointSldBody = (layerName, style) => {
    const icon = SLD_WELL_KNOWN_NAMES.has(style.icon) ? style.icon : "circle";
    const color = /^#[0-9a-fA-F]{6}$/.test(style.color || "") ? style.color : "#3388ff";
    const size = Number(style.size) > 0 ? Number(style.size) : DEFAULT_TERRAIN_MARKER_SIZE;
    const opacityNum = Number(style.opacity);
    const opacity = Number.isFinite(opacityNum) ? Math.min(1, Math.max(0, opacityNum)) : 1;
    return `<?xml version="1.0" encoding="UTF-8"?>
<StyledLayerDescriptor version="1.0.0" xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd">
  <NamedLayer>
    <Name>${layerName}</Name>
    <UserStyle>
      <FeatureTypeStyle>
        <Rule>
          <PointSymbolizer>
            <Graphic>
              <Mark>
                <WellKnownName>${icon}</WellKnownName>
                <Fill>
                  <CssParameter name="fill">${color}</CssParameter>
                  <CssParameter name="fill-opacity">${opacity}</CssParameter>
                </Fill>
                <Stroke>
                  <CssParameter name="stroke">#000000</CssParameter>
                  <CssParameter name="stroke-width">1</CssParameter>
                </Stroke>
              </Mark>
              <Size>${size}</Size>
            </Graphic>
          </PointSymbolizer>
        </Rule>
      </FeatureTypeStyle>
    </UserStyle>
  </NamedLayer>
</StyledLayerDescriptor>`;
};

export const LayerService = {
    isValidExtent(extent) {
        return (
            Array.isArray(extent) &&
            extent.length === 4 &&
            extent.every((c) => typeof c === "number" && !isNaN(c)) &&
            // <= (no <): una Capa_Terreno con una sola feature punto tiene un
            // extent degenerado (xmin===xmax, ymin===ymax) — es una ubicación
            // válida, no un extent inválido; mapService.zoomToExtent/OL
            // View.fit() lo manejan bien (centra ahí en vez de fallar).
            extent[0] <= extent[2] &&
            extent[1] <= extent[3]
        );
    },
    getLayerProvider(layer) {
        return layer?.provider?.name || "";
    },
    getLayerUrl(layer) {
        return layer.provider.url || "";
    },
    getGeoserverDataStore(layer) {
        return layer.provider.geoserverdata.datastore || "";
    },
    getGeoserverWorkspace(layer) {
        return layer.provider.geoserverdata.workspace || "";
    },
    getLayerArcGISCode(layer) {
        return layer.codigo || "0";
    },
    getLayerOrden(layer) {
        return layer.orden || 0;
    },
    getArcgisExtent(layer) {
        const queryUrl = UrlHandler.getArcgisUrl(layer) + "/query?where=1=1&returnExtentOnly=true&f=json&outSR=3857"
        console.log("Query URL:", queryUrl)
        fetch(queryUrl)
            .then((response) => response.json())
            .then((data) => {
                if (data.extent) {
                    const extent = [data.extent.xmin, data.extent.ymin, data.extent.xmax, data.extent.ymax];
                    if (this.isValidExtent(extent)) {
                        mapService.zoomToExtent(extent);
                    }
                } else {
                    console.warn("Extent no encontrado en respuesta ArcGIS:", data);
                }
            })
            .catch((error) => {
                console.error("Error al obtener el extent del layer:", error);
                return null;
            });

    },
    getArcgisImageServerExtent(layer) {
        // ImageServer has no /query endpoint; extent comes from the service metadata
        const metaUrl = UrlHandler.getArcgisUrl(layer) + "?f=json";
        console.log("Query URL:", metaUrl)
        fetch(metaUrl)
            .then((response) => response.json())
            .then((data) => {
                const fullExtent = data.fullExtent || data.extent;
                if (!fullExtent) {
                    console.warn("Extent no encontrado en respuesta ImageServer:", data);
                    return;
                }
                let extent = [fullExtent.xmin, fullExtent.ymin, fullExtent.xmax, fullExtent.ymax];
                const sr = fullExtent.spatialReference || {};
                const wkid = sr.latestWkid || sr.wkid;
                // 102100 is the legacy alias of Web Mercator (3857)
                if (wkid && wkid !== 3857 && wkid !== 102100) {
                    extent = transformExtent(extent, "EPSG:" + wkid, "EPSG:3857");
                }
                if (this.isValidExtent(extent)) {
                    mapService.zoomToExtent(extent);
                }
            })
            .catch((error) => {
                console.error("Error al obtener el extent del layer:", error);
            });
    },
    getGeoserverExtent(layer) {
        const baseUrl = UrlHandler.getBaseUrl(layer) + "/wms";
        const layersParam =
            this.getGeoserverWorkspace(layer) +
            ":" +
            this.getGeoserverDataStore(layer);
        const queryParams = {
            service: "WFS",
            version: "2.0.0",
            request: "GetFeature",
            typeNames: layersParam,
            outputFormat: "application/json",
            srsName: "EPSG:3857",
        }
        const queryUrl = baseUrl + "?" + Object.entries(queryParams)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join("&");
        console.log("Query URL:", queryUrl)
        fetch(queryUrl)
            .then((response) => {
                // GeoServer solo provisiona el feature type de una Capa_Terreno
                // con la primera respuesta aprobada materializada (ver
                // attributesTable.js::getGeoServerMetadata, mismo caso) — una
                // campaña sin respuestas aún no tiene feature type, y este WFS
                // GetFeature responde 400 con un ServiceExceptionReport (XML),
                // no JSON. No es un error real, no hay a qué extent hacer zoom
                // todavía, así que se corta acá en vez de romper en response.json().
                if (!response.ok) return null;
                return response.json();
            })
            .then((data) => {
                if (!data) return;
                // data.bbox es el bbox de la FeatureCollection completa —
                // GeoServer no siempre lo manda (depende de versión/config;
                // Capa_Terreno nunca lo trae), así que quedaba undefined
                // siempre. Se calcula la unión de la geometría de TODAS las
                // features devueltas en vez de confiar en ese campo — antes
                // se pedía count:1 (una sola feature, siempre la "primera"
                // según el orden de GeoServer) y el extent quedaba pegado
                // ahí para siempre sin importar cuántas features nuevas se
                // agregaran a la capa (Big Boss, 2026-08-04).
                const format = new GeoJSON();
                const extent = createEmptyExtent();
                (data.features || []).forEach((feature) => {
                    if (!feature.geometry) return;
                    extendExtent(extent, format.readGeometry(feature.geometry).getExtent());
                });
                if (this.isValidExtent(extent)) {
                    // minBuffer:200 (subido junto con los mismos valores de
                    // AttributesTable.vue - Big Boss, 2026-07-31: 20, luego
                    // 50, luego 100 seguían quedando muy cerca) — sigue
                    // aplicando para el caso de una sola feature, donde el
                    // extent resultante es igual de degenerado que antes.
                    mapService.zoomToExtent(padExtent(extent, { minBuffer: 200 }))
                } else {
                    console.warn("Extent inválido recibido de GeoServer:", extent);
                }
            })
            .catch((error) => {
                console.error("Error al obtener el extent del layer:", error);
                return null;
            });
    },

    async getGeoServerLayerType(layer) {
        const params = {
            workspace: this.getGeoserverWorkspace(layer),
            datastore: this.getGeoserverDataStore(layer),
            url: this.getLayerUrl(layer)
        }
        const backendURL = process.env.VUE_APP_API_URL
        // Sin el corte por tiempo, un GeoServer caído dejaba esta promesa pendiente para
        // siempre: la capa nunca aparecía y el aviso de error tampoco, porque vive en el
        // .catch. Al abortar, el tipo vuelve null y quien llama muestra el aviso.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), GEOSERVER_LAYERTYPE_TIMEOUT_MS);
        return fetch(backendURL + "api/v1/geoserver/layerType", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(params),
            signal: controller.signal,
        })
            .then((response) => {
                // fetch no falla ante un 4xx/5xx: sin este chequeo se leía el cuerpo del
                // error y el tipo salía undefined, indistinguible de una respuesta válida.
                if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
                return response.json();
            })
            .then((data) => {
                console.log("Data:", data)
                return data.layerType
            })
            .catch((error) => {
                console.error("Error al obtener el tipo del layer:", error);
                return null;
            })
            .finally(() => clearTimeout(timeoutId));
    },

    // El filtro viaja por GET dentro de la URL del GetMap; sobre ~8 KB el
    // servidor rechaza con 400, así que hay que acotar la cantidad de
    // vértices del anillo antes de armar el WKT.
    _simplifiedRingWkt(ring) {
        const MAX_CQL_VERTICES = 200;
        if (ring.length > MAX_CQL_VERTICES) {
            const originalLength = ring.length;
            let tolerance = 10; // metros en EPSG:3857
            let simplified = ring;
            const srcPolygon = new Polygon([ring]);
            while (simplified.length > MAX_CQL_VERTICES && tolerance <= 10000) {
                simplified = srcPolygon.simplify(tolerance).getCoordinates()[0];
                tolerance *= 2;
            }
            if (simplified.length > MAX_CQL_VERTICES) {
                const step = Math.ceil(simplified.length / MAX_CQL_VERTICES);
                simplified = simplified.filter((_, i) => i % step === 0);
            }
            ring = simplified;
            console.log(`CQL filter: anillo simplificado de ${originalLength} a ${ring.length} vértices`);
        }
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
            ring = [...ring, first];
        }
        return ring.map(c => `${Math.round(c[0])} ${Math.round(c[1])}`).join(",");
    },
    // Helper to build CQL filter from geometry
    buildCqlFilter(geometry) {
        if (!geometry || !geometry.coordinates) return "";
        try {
            const type = geometry.type;
            const coords = geometry.coordinates;
            // Las geometrías del visor vienen en EPSG:3857; sin declarar el
            // SRID GeoServer las asume en el SRS nativo de la capa y el
            // filtro no intersecta nada (capa invisible).
            if (type === "Polygon") {
                const coordStr = this._simplifiedRingWkt(coords[0]);
                return `INTERSECTS(the_geom, SRID=3857;POLYGON((${coordStr})))`;
            }
            // Zonas no anidadas (ej. bandas horarias de la pluma de dispersión):
            // cada parte se declara por separado en vez de recortar solo con
            // la más externa, que dejaría fuera las zonas interiores.
            if (type === "MultiPolygon") {
                const polys = coords.map(poly => `((${this._simplifiedRingWkt(poly[0])}))`).join(",");
                return `INTERSECTS(the_geom, SRID=3857;MULTIPOLYGON(${polys}))`;
            }
        } catch (e) {
            console.error("Error building CQL filter:", e);
        }
        return "";
    },
    buildGeoserverVectorLayer(layer, filterGeometry = null) {
        const url = UrlHandler.getBaseUrl(layer) + "/wms";
        const layersParam =
            this.getGeoserverWorkspace(layer) +
            ":" +
            this.getGeoserverDataStore(layer);

        const params = {
            LAYERS: layersParam,
            TILED: true,
            VERSION: "1.1.1",
        };

        // layer.provider.style solo viene presente en Capa_Terreno (ver
        // terrainGeoserverDescriptor en TerrainSection.vue) — cualquier otra
        // capa geoserver sigue usando el estilo por defecto del feature
        // type, como siempre.
        if (layer.provider && layer.provider.style) {
            params.SLD_BODY = buildPointSldBody(layersParam, layer.provider.style);
        }

        if (filterGeometry) {
            console.log("Building CQL filter with geometry:", filterGeometry);
            const cql = this.buildCqlFilter(filterGeometry);
            if (cql) {
                params.CQL_FILTER = cql;
                console.log("Applying CQL Filter:", cql);
            } else {
                console.warn("CQL Filter returned empty string.");
            }
        } else {
            console.log("No filterGeometry passed to buildGeoserverVectorLayer");
        }

        const wmsSource = new TileWMS({
            url,
            params: params,
            serverType: "geoserver",
            crossOrigin: "anonymous",
            tileLoadFunction: longRequestTileLoader,
        });
        const layerToAdd = new TileLayer({
            source: wmsSource,
            opacity: 1,
            id: layer.id,
        })
        console.log("Legend URL:", wmsSource.getLegendUrl());
        layerToAdd.set('provider', 'geoserver');
        layerToAdd.set('layerType', 'vector');
        layerToAdd.set('detachLoadTracking', attachTileLoadTracking(wmsSource, { layerId: layer.id, layerName: layer.name }));
        return layerToAdd
    },
    buildGeoserverRasterLayer(layer) {
        const baseURL = UrlHandler.getBaseUrl(layer)
        const workspace = this.getGeoserverWorkspace(layer)
        const datastore = this.getGeoserverDataStore(layer)
        const layersParam = `${workspace}:${datastore}`
        const url = `${baseURL}/wms/${workspace}`
        const source = new ImageWMS({
            url: url,
            params: {
                'LAYERS': layersParam,
                'FORMAT': 'image/png',
                'VERSION': '1.1.1'
            }
        })
        const layerToAdd = new ImageLayer({
            source: source,
            opacity: 1,
            id: layer.id,
        });
        layerToAdd.set('provider', 'geoserver');
        layerToAdd.set('layerType', 'raster');
        layerToAdd.set('detachLoadTracking', attachRasterLoadTracking(source, { layerId: layer.id, layerName: layer.name }));
        return layerToAdd
    },



    buildGeoserverLayer(layer, layerType, filterGeometry = null) {
        console.log("Layer tipo:", layerType)
        if (layerType === "vector") {
            return this.buildGeoserverVectorLayer(layer, filterGeometry)
        }
        if (layerType === "raster") {
            return this.buildGeoserverRasterLayer(layer)
        }

        return null
    },
    // knownLayerType salta la consulta a GeoServer (vía backend) para
    // determinar vector/raster — usado por Capa_Terreno (PB-24_v2), que
    // siempre es puntual/vectorial por diseño del dominio (nunca hay que
    // preguntarle a GeoServer). Esto también evita un problema real solo en
    // desarrollo local: esa consulta la hace el backend usando la MISMA url
    // que ve el navegador (layer.provider.url), pero el backend corre en un
    // contenedor Docker donde "localhost" es el propio backend, no GeoServer
    // — en producción ambos alcanzan la misma URL pública y esto no aplica,
    // pero igual no tiene sentido pagar ese round-trip por un dato que ya se
    // conoce de antemano.
    async addLayerGeoserver(layer, filterGeometry = null, doZoom = true, knownLayerType = null, activationToken = null) {
        const activation = activationToken || startLayerActivation(layer.id);
        if (!activationToken) beginLayerLoad({ layerId: layer.id });
        const ordenLayerInt = parseInt(this.getLayerOrden(layer));
        const layerType = knownLayerType || await this.getGeoServerLayerType(layer)
        if (!isCurrentLayerActivation(layer.id, activation)) return null;
        console.log("Layer tipo identificada:", layerType)
        if (layerType === null || layerType === undefined) {
            // Antes se salía en silencio: la capa no aparecía y el usuario no tenía forma
            // de saber por qué. Se avisa igual que cuando un servicio ArcGIS no responde.
            endLayerLoad({ layerId: layer.id });
            store.dispatch("toast/show", {
                message: `La capa "${layer.name}" no está disponible: el servicio GeoServer no responde o la capa no existe.`,
                type: "error",
            });
            finishLayerActivation(layer.id, activation);
            return null;
        }
        const layerToAdd = this.buildGeoserverLayer(layer, layerType, filterGeometry)
        console.log("Layer creada:", layerToAdd)
        if (layerType === "vector" && doZoom) {
            this.getGeoserverExtent(layer)
        }
        layerToAdd.setZIndex(ordenLayerInt);
        mapService.addLayerToMap(layerToAdd);
        finishLayerActivation(layer.id, activation);
        attributesTableService.applySavedFilterToMap(layer).catch(error => console.warn("No se pudo restaurar el filtro de la capa:", error));
        return layerToAdd;
    },
    addLayerArcGISImageServer(layer, doZoom = true) {
        const url = UrlHandler.getArcgisUrl(layer);
        const ordenLayerInt = parseInt(this.getLayerOrden(layer));
        const layerSource = new ImageArcGISRest({
            url: url,
        })
        const layerToAdd = new ImageLayer({
            source: layerSource,
            opacity: 1,
            id: layer.id,
        })
        layerToAdd.set('layerType', 'raster');
        layerToAdd.set('detachLoadTracking', attachRasterLoadTracking(layerSource, { layerId: layer.id, layerName: layer.name }));
        if (doZoom) {
            this.getArcgisImageServerExtent(layer)
        }
        layerToAdd.setZIndex(ordenLayerInt);
        mapService.addLayerToMap(layerToAdd);
        return layerToAdd;
    },
    addLayerArcGISMapServer(layer, filterGeometry = null, doZoom = true) {
        const url = UrlHandler.getBaseUrl(layer);
        const codigoLayer = this.getLayerArcGISCode(layer);
        const ordenLayerInt = parseInt(this.getLayerOrden(layer));

        const params = {
            LAYERS: "show:" + codigoLayer,
        };

        // Standard TileArcGISRest for all layers
        const layerSource = new TileArcGISRest({
            url: url,
            params: params,
            tileLoadFunction: longRequestTileLoader,
        })
        const layerToAdd = new TileLayer({
            source: layerSource,
            opacity: 1,
            id: layer.id,
        })
        layerToAdd.set('provider', 'arcgis');
        layerToAdd.set('layerType', 'vector');
        layerToAdd.set('codigo', codigoLayer);
        layerToAdd.set('detachLoadTracking', attachTileLoadTracking(layerSource, { layerId: layer.id, layerName: layer.name }));
        if (doZoom) {
            this.getArcgisExtent(layer)
        }
        layerToAdd.setZIndex(ordenLayerInt);
        mapService.addLayerToMap(layerToAdd, filterGeometry); // Pass filterGeometry for client-side clipping
        attributesTableService.applySavedFilterToMap(layer).catch(error => console.warn("No se pudo restaurar el filtro de la capa:", error));
        return layerToAdd;
    },


    addLayerArcGISFeatureServer(layer, doZoom = true) {
        const ordenLayerInt = parseInt(this.getLayerOrden(layer));
        const fullUrl = UrlHandler.getArcgisUrl(layer);
        console.log("Full url:", fullUrl)
        const format = new EsriJSON();
        let layerSource;
        layerSource = new VectorSource({
            format,
            loader: function (extent, resolution, projection, success, failure) {
                const filterRevision = layerSource.get('attributeFilterRevision') || 0;
                // ArcGIS Server only wants the numeric portion of the projection ID.
                const srid = projection
                    .getCode()
                    .split(/:(?=\d+$)/)
                    .pop();
                const params = new URLSearchParams({
                    f: 'json',
                    returnGeometry: 'true',
                    spatialRel: 'esriSpatialRelIntersects',
                    geometry: JSON.stringify({
                        xmin: extent[0], ymin: extent[1], xmax: extent[2], ymax: extent[3],
                        spatialReference: { wkid: Number(srid) },
                    }),
                    geometryType: 'esriGeometryEnvelope',
                    inSR: srid,
                    outFields: '*',
                    where: layerSource.get('attributeFilterWhere') || '1=1',
                    outSR: srid,
                });
                fetch(`${fullUrl}/query`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: params.toString(),
                }).then(response => {
                    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
                    return response.json();
                }).then(data => {
                    if (data.error) throw new Error(data.error.message || 'ArcGIS rechazó la consulta');
                    const features = format.readFeatures(data, { featureProjection: projection });
                    if ((layerSource.get('attributeFilterRevision') || 0) !== filterRevision) return;
                    layerSource.addFeatures(features);
                    if (success) success(features);
                }).catch(error => {
                    console.error('No se pudo cargar la capa FeatureServer:', error);
                    layerSource.removeLoadedExtent(extent);
                    if (failure) failure();
                });
            },
            strategy: bboxStrategy,
        });

        const layerToAdd = new VectorLayer({
            source: layerSource,
            opacity: 1,
            id: layer.id,
        })
        layerToAdd.set('provider', 'arcgis');
        layerToAdd.set('layerType', 'vector');
        layerToAdd.set('id', layer.id);
        layerToAdd.set('detachLoadTracking', attachVectorLoadTracking(layerSource, { layerId: layer.id, layerName: layer.name }));
        if (doZoom) {
            this.getArcgisExtent(layer)
        }
        layerToAdd.setZIndex(ordenLayerInt);
        mapService.addLayerToMap(layerToAdd);
        attributesTableService.applySavedFilterToMap(layer).catch(error => console.warn("No se pudo restaurar el filtro de la capa:", error));
        return layerToAdd;
    },
    async checkUrlExists(layer) {
        const baseUrl = UrlHandler.getArcgisUrl(layer) + "?f=json"
        console.log("Full url:", baseUrl)
        try {
            const response = await fetch(baseUrl)
            if (!response.ok) {
                return false
            }
            const data = await response.json()
            return data.error === undefined
        } catch (error) {
            console.warn("No se pudo verificar la capa:", layer.name, baseUrl, error)
            return false
        }
    },
    async addLayerArcgis(layer, filterGeometry = null, doZoom = true, activationToken = null) {
        const activation = activationToken || startLayerActivation(layer.id);
        if (!activationToken) beginLayerLoad({ layerId: layer.id });
        const baseUrl = UrlHandler.getBaseUrl(layer).toLowerCase();
        const exists = await this.checkUrlExists(layer)
        if (!isCurrentLayerActivation(layer.id, activation)) return null;
        console.log("Existe:", exists)
        if (!exists) {
            endLayerLoad({ layerId: layer.id });
            store.dispatch("toast/show", {
                message: `La capa "${layer.name}" no está disponible: el servicio ArcGIS no responde.`,
                type: "error",
            });
            finishLayerActivation(layer.id, activation);
            return null;
        }
        let layerToAdd = null;
        if (baseUrl.endsWith("/imageserver")) {
            layerToAdd = this.addLayerArcGISImageServer(layer, doZoom);
        } else if (baseUrl.endsWith("/mapserver")) {
            layerToAdd = this.addLayerArcGISMapServer(layer, filterGeometry, doZoom);
        } else if (baseUrl.endsWith("/featureserver")) {
            layerToAdd = this.addLayerArcGISFeatureServer(layer, doZoom);
        } else {
            endLayerLoad({ layerId: layer.id });
            store.dispatch("toast/show", {
                message: `La capa "${layer.name}" no tiene un servicio ArcGIS compatible.`,
                type: "error",
            });
        }
        finishLayerActivation(layer.id, activation);
        return layerToAdd;
    },
    addLayerToMap(layer, filterGeometry = null, doZoom = true) {
        const activation = startLayerActivation(layer.id);
        const provider = this.getLayerProvider(layer);
        switch (provider) {
            case "geoserver":
                // Antes de que exista la fuente hay que preguntarle al backend si la capa
                // es ráster o vectorial; el aviso arranca acá para cubrir esa espera.
                beginLayerLoad({ layerId: layer.id });
                return this.addLayerGeoserver(layer, filterGeometry, doZoom, null, activation);
            case "arcgis":
                // Ídem: primero se verifica que el servicio ArcGIS responda.
                beginLayerLoad({ layerId: layer.id });
                return this.addLayerArcgis(layer, filterGeometry, doZoom, activation);
            case IMPORT_PROVIDER: {
                const existing = importedLayerRegistry.get(layer.id);
                if (existing) {
                    if (!isCurrentLayerActivation(layer.id, activation)) return null;
                    if (!mapService.layerExists(layer.id)) mapService.addLayerToMap(existing.layer);
                    finishLayerActivation(layer.id, activation);
                    return existing;
                }
                return this.loadLayerFromBackend({
                    id: layer.fileId,
                    name: layer.fileName,
                    manifest: {
                        datasets: [{ key: layer.datasetKey, options: layer.importOptions || {} }],
                    },
                }, layer, doZoom, activation);
            }
            default:
                console.log("Provider no soportado:", provider);
                finishLayerActivation(layer.id, activation);
                return null;
        }
    },
    async uploadShpKmz(file, idMap, manifest = null) {
        console.log("Subiendo archivo...", file.name, idMap);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("idMap", idMap);
        if (manifest) formData.append("manifest", JSON.stringify(manifest));
        const url = process.env.VUE_APP_API_URL + "api/v1/uploads";
        console.log("URL para subida:", url);
        try {
            const response = await fetch(url, {
                method: "POST",
                credentials: 'include',
                body: formData,
            });
            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.error || payload.message || "No se pudo importar el archivo");
            }
            console.log("Response:", response);
            return await response.json();
        } catch (error) {
            console.error("Error al subir archivo:", error);
            throw error;
        }
    },
    removeLayerByID(layerID) {
        cancelLayerActivation(layerID);
        mapService.removeLayerFromMapByID(layerID);
    },
    deactivateImportedLayer(layerID) {
        cancelLayerActivation(layerID);
        mapService.removeLayerFromMapByID(layerID);
    },
    destroyImportedLayer(layerID) {
        cancelLayerActivation(layerID);
        mapService.removeLayerFromMapByID(layerID);
        importedLayerRegistry.remove(layerID);
    },
    clearImportedFileCache(fileId) {
        parsedImportedFiles.delete(String(fileId));
    },
    async getLayerTypeArcgis(layer) {
        if (this.getLayerProvider(layer) !== "arcgis") {
            return "";
        }
        if (layerTypeArcgisCache.has(layer.id)) {
            return layerTypeArcgisCache.get(layer.id);
        }
        const layerUrl = UrlHandler.getArcgisUrl(layer);
        const metadataUrl = layerUrl + "?f=pjson";
        try {
            const response = await fetch(metadataUrl);
            if (!response.ok) {
                console.warn("No se pudo obtener metadata de capa:", layer.name, metadataUrl);
                return "";
            }
            const metadata = await response.json();
            const geometryType = metadata.geometryType || "";
            layerTypeArcgisCache.set(layer.id, geometryType);
            return geometryType;
        } catch (error) {
            console.warn("No se pudo obtener metadata de capa:", layer.name, metadataUrl, error);
            return "";
        }
    },
    async filterLayersByInfluence(categories, influenceType) {
        const typeLayerDictionary = {
            "esriGeometryPoint": "Radial",
            "esriGeometryMultipoint": "Radial",
            "esriGeometryPolygon": "Poligonal",
            "esriGeometryPolyline": "Lineal",
            "Point": "Radial",
            "MultiPoint": "Radial",
            "LineString": "Lineal",
            "MultiLineString": "Lineal",
            "Polygon": "Poligonal",
            "MultiPolygon": "Poligonal",
        };
        let categoriesFiltered = [];
        for (const category of categories) {
            let layersFiltered = [];
            for (const layer of category.layers) {
                let geometryTypes = [];
                if (this.getLayerProvider(layer) === IMPORT_PROVIDER) {
                    const registeredTypes = importedLayerRegistry.get(layer.id)?.metadata?.geometryTypes || [];
                    geometryTypes = Array.from(new Set([...(layer.geometryTypes || []), ...registeredTypes]));
                } else {
                    const layerTypeArcgis = await this.getLayerTypeArcgis(layer);
                    if (layerTypeArcgis) geometryTypes = [layerTypeArcgis];
                }
                if (!geometryTypes.some(type => typeLayerDictionary[type] === influenceType)) continue;
                layersFiltered.push(layer);
            }
            if (layersFiltered.length === 0) {
                continue;
            }
            categoriesFiltered.push({
                name: category.name,
                layers: layersFiltered
            });
        }
        return categoriesFiltered;
    },
    layerExists(layerId) {
        return mapService.layerExists(layerId);
    },
    async getUploadedFilesByMap(idMapa) {
        console.log("Obteniendo archivos subidos desde el backend...", idMapa);
        const url = process.env.VUE_APP_API_URL + "api/v1/uploads/map/" + idMapa;
        console.log("URL para obtener archivos subidos:", url);
        try {
            const response = await fetch(url, { credentials: 'include' });
            if (!response.ok) {
                throw new Error("Error en la petición: " + response.statusText);
            }
            const data = await response.json();
            console.log("Data del upload:", data);
            return data;
        } catch (error) {
            console.error("Error al obtener archivos subidos:", error);
            return [];
        }
    },
    async deleteFileByID(fileId) {
        const url = process.env.VUE_APP_API_URL + "api/v1/uploads/" + fileId;
        console.log("URL para eliminar archivo:", url);
        try {
            const response = await fetch(url, {
                method: "DELETE",
                credentials: 'include',
            });
            if (!response.ok) {
                throw new Error("Error en la petición: " + response.statusText);
            }
            console.log("Archivo eliminado correctamente.");
        } catch (error) {
            console.error("Error al eliminar archivo:", error);
            throw error; // Re-throw to allow component to handle fallback (though component currently catches without showing error to user, but logic flow relies on completion)
        }
    },
    async loadLayerFromBackend(fileObj, requestedDescriptor = null, doZoom = true, activationToken = null) {
        const descriptor = requestedDescriptor || createImportedDescriptor(fileObj, true);
        const activation = activationToken || startLayerActivation(descriptor.id);
        const existing = importedLayerRegistry.get(descriptor.id);
        if (existing) {
            if (!isCurrentLayerActivation(descriptor.id, activation)) return null;
            if (!mapService.layerExists(descriptor.id)) mapService.addLayerToMap(existing.layer);
            finishLayerActivation(descriptor.id, activation);
            return existing;
        }
        const url = process.env.VUE_APP_API_URL + "api/v1/uploads/" + fileObj.id + "/download";
        console.log("Descargando capa desde:", url);
        // Descargar y procesar el archivo puede tardar; el aviso cubre toda esa espera.
        beginLayerLoad({ layerId: descriptor.id });
        try {
            const cacheKey = String(fileObj.id);
            if (!parsedImportedFiles.has(cacheKey)) {
                parsedImportedFiles.set(cacheKey, (async () => {
                    const response = await fetch(url, { credentials: 'include' });
                    if (!response.ok) throw new Error("Fallo la descarga del archivo");
                    const blob = await response.blob();
                    const file = new File([blob], fileObj.name);
                    const manifestDataset = (fileObj.manifest?.datasets || []).find(item => item.key === descriptor.datasetKey) ||
                        (fileObj.manifest?.datasets || [])[0];
                    return parseImportedFile(file, manifestDataset?.options || {});
                })().catch(error => {
                    parsedImportedFiles.delete(cacheKey);
                    throw error;
                }));
            }
            const parsed = await parsedImportedFiles.get(cacheKey);
            if (!isCurrentLayerActivation(descriptor.id, activation)) return null;
            const parsedDataset = parsed.datasets.find(item => item.key === descriptor.datasetKey) || parsed.datasets[0];
            if (!parsedDataset) throw new Error("No se encontró la subcapa importada");
            return this.loadParsedImportedLayer(descriptor, parsedDataset, doZoom);
        } catch (e) {
            if (!isCurrentLayerActivation(descriptor.id, activation)) return null;
            console.error("Error cargando capa desde backend:", e);
            throw e;
        } finally {
            if (isCurrentLayerActivation(descriptor.id, activation)) {
                endLayerLoad({ layerId: descriptor.id });
                finishLayerActivation(descriptor.id, activation);
            }
        }
    },
    loadParsedImportedLayer(descriptor, dataset, doZoom = true) {
        const existing = importedLayerRegistry.get(descriptor.id);
        if (existing) {
            if (!mapService.layerExists(descriptor.id)) mapService.addLayerToMap(existing.layer);
            return existing;
        }
        const vectorSource = new VectorSource({ features: dataset.features });
        const layer = new VectorLayer({ source: vectorSource, name: descriptor.id });
        layer.set("id", descriptor.id);
        if (dataset.cleanup) layer.set("importCleanup", dataset.cleanup);
        mapService.addLayerToMap(layer);
        const extent = vectorSource.getExtent();
        if (doZoom && this.isValidExtent(extent)) mapService.zoomToExtent(extent);
        return importedLayerRegistry.register(descriptor, layer);
    },
    async getPoblationLayers(poblationType) {
        const url = process.env.VUE_APP_API_URL + "api/v1/layers/poblation/" + poblationType;
        console.log("URL para obtener capas de población:", url);
        try {
            const response = await fetch(url, { credentials: 'include' });
            if (!response.ok) {
                throw new Error("Error en la petición: " + response.statusText);
            }
            const data = await response.json();
            console.log("Data de capas de población:", data);
            // El backend responde 200 con body null cuando no hay capas
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error("Error al obtener capas de población:", error);
            return [];
        }
    },
    async getVulnerableLayers(idMap) {
        let url = process.env.VUE_APP_API_URL + "api/v1/layers/vulnerable";
        if (idMap) {
            url += "?idMap=" + encodeURIComponent(idMap);
        }
        console.log("URL para obtener capas vulnerables:", url);
        try {
            const response = await fetch(url, { credentials: 'include' });
            if (!response.ok) {
                throw new Error("Error en la petición: " + response.statusText);
            }
            const data = await response.json();
            console.log("Data de capas vulnerables:", data);
            // El backend responde 200 con body null cuando no hay capas
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error("Error al obtener capas vulnerables:", error);
            return [];
        }
    }
};
